
import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { MessageSquare, Send, Lightbulb } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface ClarificationInputProps {
  onSubmitClarification: (clarification: string, type: ClarificationType) => void;
  isSubmitting?: boolean;
  suggestions?: string[];
}

export type ClarificationType = 'tone_adjustment' | 'add_context' | 'strategy_change' | 'general';

const ClarificationInput = ({ 
  onSubmitClarification, 
  isSubmitting = false,
  suggestions = []
}: ClarificationInputProps) => {
  const [clarification, setClarification] = useState("");
  const { toast } = useToast();

  const handleSubmit = () => {
    if (!clarification.trim()) {
      toast({
        variant: "destructive",
        title: "Missing clarification",
        description: "Please provide clarification details before submitting."
      });
      return;
    }

    // Always use 'general' type - let AI determine the intent from the text
    onSubmitClarification(clarification.trim(), 'general');
    setClarification("");
  };

  const handleSuggestionClick = (suggestion: string) => {
    setClarification(suggestion);
  };

  return (
    <Card className="border-blue-200 bg-blue-50/50">
      <CardHeader className="pb-3">
        <CardDescription>
          Provide additional context or adjustments to improve the AI response
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Suggestions */}
        {suggestions.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Lightbulb className="h-4 w-4 text-yellow-600" />
              Suggestions:
            </div>
            <div className="grid gap-2">
              {suggestions.map((suggestion, index) => (
                <div
                  key={index}
                  className="p-2 bg-white border border-gray-200 rounded-lg cursor-pointer hover:border-blue-300 hover:bg-blue-50 text-sm"
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  {suggestion}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Clarification Input */}
        <div className="space-y-2">
          <label htmlFor="clarification" className="text-sm font-medium">
            Your clarification or adjustment request:
          </label>
          <Textarea
            id="clarification"
            placeholder="e.g., 'Make the tone more aggressive' or 'Add information about warranty coverage' or 'Focus more on legal rights'"
            value={clarification}
            onChange={(e) => setClarification(e.target.value)}
            className="min-h-[80px]"
          />
        </div>

        {/* Submit Button */}
        <Button 
          onClick={handleSubmit}
          disabled={isSubmitting || !clarification.trim()}
          className="w-full"
        >
          <Send className="h-4 w-4 mr-2" />
          {isSubmitting ? "Processing..." : "Apply Clarification"}
        </Button>
      </CardContent>
    </Card>
  );
};

export default ClarificationInput;
